/**
 * One-time browser → server migration for F4 / SPEC-all-data-on-pi. On
 * first load after the server-first user-data store ships, any known
 * localStorage key that holds data is uploaded into its matching
 * `/api/userdata` namespace — provided that namespace is still empty on
 * the server. After a pass in which every upload attempt succeeded (or
 * was a legitimate skip) a flag is written so the migration never runs
 * again (idempotent). If ANY server call failed (Pi offline, locked),
 * the flag is left unset so the migration retries on the next boot.
 *
 * Explicitly EXCLUDED:
 *   - The M0.7 advanced-mode padlock — it lives in `sessionStorage`
 *     (`reefapp.advancedMode`), not localStorage, and is intentionally
 *     ephemeral (`.clinerules` forbids touching it).
 *   - Dashboard layouts (`reefapp.dashboards*`) — already server-backed
 *     by their own DashboardRegistry migration.
 *   - Cosmetic UI prefs (sidebar collapsed, active tabs) — browser-local
 *     by design per the spec.
 *
 * Namespaces must be URL/file-safe (`^[A-Za-z0-9_-]{1,64}$`), so the
 * dotted localStorage keys are mapped to hyphenated namespace names.
 * Legacy browser copies are NEVER deleted (rule 7: local data is the
 * offline fallback).
 */
import { userDataApi } from './userdata-api';

/** localStorage flag marking the one-time migration complete. */
export const USERDATA_MIGRATION_FLAG = 'reefapp.userdata.migrated';

/** Legacy per-slug calibration checklist key prefix. */
export const CALIBRATION_LEGACY_PREFIX = 'reefapp.stockSolutions.calibration.';

/** Consolidated server namespace for all calibration checklists. */
export const CALIBRATION_NAMESPACE = 'stock-calibration';

export interface MigrationMapping {
  /** Source key in `localStorage`. */
  readonly localStorageKey: string;
  /** Destination `/api/userdata` namespace (URL/file-safe). */
  readonly namespace: string;
}

/**
 * The known browser-only user DATA keys worth preserving (test-kit
 * results, parameter/manual logs, unit preference, stock-solution
 * favorites/history/volumes, preferred commercial products). Derived
 * from a full audit of `localStorage` usage in `apps/web/src`.
 */
export const DEFAULT_MIGRATION_MAPPINGS: readonly MigrationMapping[] = [
  // Test-kit results (guided + quick log), audit trail and reminders.
  { localStorageKey: 'reefapp.testKits.logs', namespace: 'test-kit-logs' },
  { localStorageKey: 'reefapp.testKits.history', namespace: 'test-kit-history' },
  {
    localStorageKey: 'reefapp.testKits.reagentDates',
    namespace: 'test-kit-reagent-dates',
  },
  {
    localStorageKey: 'reefapp.testKits.reminderSettings',
    namespace: 'test-kit-reminder-settings',
  },
  // Manual parameter entries (parameter logs).
  { localStorageKey: 'reefapp.manualStats', namespace: 'manual-stats' },
  // Stock-solutions: unit preference, favorites, dosing history,
  // persisted tank volumes.
  {
    localStorageKey: 'reefapp.stockSolutions.unitPreference',
    namespace: 'unit-preference',
  },
  {
    localStorageKey: 'reefapp.stockSolutions.favorites',
    namespace: 'stock-favorites',
  },
  {
    localStorageKey: 'reefapp.stockSolutions.history',
    namespace: 'stock-history',
  },
  {
    localStorageKey: 'reefapp.stockSolutions.tankGallons',
    namespace: 'stock-tank-gallons',
  },
  {
    localStorageKey: 'reefapp:tank-volume-gal',
    namespace: 'tank-volume',
  },
  // Preferred commercial products (manual-readings action plans).
  {
    localStorageKey: 'reefapp:preferred-products',
    namespace: 'preferred-products',
  },
];

export interface MigrationReport {
  /** Namespaces that received uploaded data this pass. */
  readonly migrated: string[];
  /** Namespaces skipped (no source data, or server already had data). */
  readonly skipped: string[];
  /** Namespaces whose upload FAILED (Pi unreachable / rejected). */
  readonly failed: string[];
  /** `true` when the migration flag was already set (no work done). */
  readonly alreadyDone: boolean;
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'key' | 'length'>;
type UserDataApiLike = Pick<typeof userDataApi, 'get' | 'put'>;

export interface RunMigrationOptions {
  readonly storage?: StorageLike;
  readonly api?: UserDataApiLike;
  readonly mappings?: readonly MigrationMapping[];
}

/** Parse a stored string as JSON, falling back to the raw string. */
function parseStored(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    // Plain-string values (e.g. unit preference 'L', sidebar '1').
    return raw;
  }
}

/**
 * Collect every legacy per-slug calibration checklist
 * (`reefapp.stockSolutions.calibration.<slug>`) into one record for the
 * consolidated `stock-calibration` namespace.
 */
function collectLegacyCalibration(storage: StorageLike): Record<string, unknown> {
  const record: Record<string, unknown> = {};
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key === null || !key.startsWith(CALIBRATION_LEGACY_PREFIX)) continue;
    const slug = key.slice(CALIBRATION_LEGACY_PREFIX.length);
    if (slug === '') continue;
    const raw = storage.getItem(key);
    if (raw === null || raw === '') continue;
    record[slug] = parseStored(raw);
  }
  return record;
}

/**
 * Run the one-time migration. Safe to call on every app boot: it
 * short-circuits once the flag is set, and even before then it only
 * uploads a namespace whose server copy is still empty. The flag is
 * only written after a fully clean pass, so an offline boot retries.
 */
export async function runUserDataMigration(
  options: RunMigrationOptions = {},
): Promise<MigrationReport> {
  const storage: StorageLike =
    options.storage ?? globalThis.localStorage;
  const api: UserDataApiLike = options.api ?? userDataApi;
  const mappings = options.mappings ?? DEFAULT_MIGRATION_MAPPINGS;

  const migrated: string[] = [];
  const skipped: string[] = [];
  const failed: string[] = [];

  if (storage.getItem(USERDATA_MIGRATION_FLAG) === '1') {
    return { migrated, skipped, failed, alreadyDone: true };
  }

  /** Upload `value` into `namespace` iff the server copy is empty. */
  const uploadIfServerEmpty = async (
    namespace: string,
    value: unknown,
  ): Promise<void> => {
    // Only upload when the server namespace is still empty — never
    // clobber data already living on the Pi.
    const existing = await api.get(namespace);
    if (!existing.ok) {
      failed.push(namespace);
      return;
    }
    if (existing.value.data !== null) {
      skipped.push(namespace);
      return;
    }
    const put = await api.put(namespace, value);
    if (put.ok) migrated.push(namespace);
    else failed.push(namespace);
  };

  for (const { localStorageKey, namespace } of mappings) {
    const raw = storage.getItem(localStorageKey);
    if (raw === null || raw === '') {
      skipped.push(namespace);
      continue;
    }
    await uploadIfServerEmpty(namespace, parseStored(raw));
  }

  // Consolidate the legacy per-slug calibration checklists into the
  // single `stock-calibration` namespace.
  const calibration = collectLegacyCalibration(storage);
  if (Object.keys(calibration).length > 0) {
    await uploadIfServerEmpty(CALIBRATION_NAMESPACE, calibration);
  } else {
    skipped.push(CALIBRATION_NAMESPACE);
  }

  // Only mark the migration done when every attempt either succeeded or
  // was a legitimate skip — a failed call (Pi offline / locked) leaves
  // the flag unset so the next boot retries.
  if (failed.length === 0) {
    storage.setItem(USERDATA_MIGRATION_FLAG, '1');
  }
  return { migrated, skipped, failed, alreadyDone: false };
}
